// hooks/use-realtime-messages.js — Polls for multi-participant messages
// ═══════════════════════════════════════════════════════════════════
// Extracted from chat-space-raw.js / chat-workspace-raw.js.
// Polls /api/user/messages every N seconds and merges server data
// with local streaming bubbles (local takes precedence so typing
// animation is not interrupted).
//
// Also re-fetches immediately when the tab gains focus or becomes
// visible, so an invitee returning from another tab sees fresh
// state without having to manually refresh.
//
// Usage:
//   useRealtimeMessages({
//     sessionId: spaceSession.id,
//     supabase,
//     messages,
//     setMessages,
//     interval: 1500,        // optional, default 1.5s
//     enabled: true,         // optional, default true
//   });
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';

export function useRealtimeMessages({
  sessionId,
  supabase,
  messages,
  setMessages,
  interval = 1500,
  enabled = true,
}) {
  const lastCountRef = useRef(0);
  const lastSignatureRef = useRef('');

  useEffect(() => {
    if (!sessionId || !supabase || !enabled) return;
    let active = true;
    lastCountRef.current = 0;
    lastSignatureRef.current = '';

    async function loadMessages() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(
          `/api/user/messages?sessionId=${encodeURIComponent(sessionId)}`,
          { headers: { 'Authorization': `Bearer ${session?.access_token || ''}` } },
        );
        if (res.ok && active) {
          const { messages: data } = await res.json();
          if (!data) return;
          // Detect change by both length AND content signature so a
          // file upload (which keeps length but adds a new message
          // since the previous poll) still triggers a re-render. The
          // signature is cheap: id+content length of the last few rows.
          const sig = data.length + ':' + data.slice(-3).map(d =>
            `${d.id}:${(d.content || '').length}`).join('|');
          if (data.length !== lastCountRef.current || sig !== lastSignatureRef.current) {
            lastCountRef.current = data.length;
            lastSignatureRef.current = sig;
            setMessages(prev => {
              // Merge server data with local streaming bubbles, deduping by id.
              // Local streaming version takes precedence (so the typing animation
              // is not interrupted by the server's full text).
              const localStreaming = prev.filter(m => m.streaming);
              const merged = data.map(d => {
                const local = localStreaming.find(s => s.id === d.id);
                return local || d;
              });
              // Append any streaming items not yet in server data (newest)
              localStreaming.forEach(s => {
                if (!data.some(d => d.id === s.id)) merged.push(s);
              });
              return merged;
            });
          }
        }
      } catch (e) {
        console.error('[useRealtimeMessages] load failed:', e.message);
      }
    }

    loadMessages();
    const timer = setInterval(loadMessages, interval);

    // Snap-refresh on focus / visibility change — the invitee returning
    // to the tab should see the latest state immediately, not wait for
    // the next poll tick.
    const onFocus = () => loadMessages();
    const onVisible = () => { if (document.visibilityState === 'visible') loadMessages(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [sessionId, enabled, interval]);
}
